import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemaInterfaceExtendingSchema, TagSchema} from 'constants/RegistryConstants';
import {LookupTableInsertType} from 'notebook/integrations/lookup/LookupStructuredTableAdapter';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import AllStorablesSchemaSelector from 'samples/AllStorablesSchemaSelector';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {isSchemaTypeFilterOption} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {isSchemaInterface} from 'schemas/SchemaPredicates';
import TagSchemaOrInterfaceSelector from 'schemas/shared/TagSchemaOrInterfaceSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';

function getUserTextForLookupTableType(type: LookupTableInsertType): string {
  return type === LookupTableInsertType.EXISTING_BIOENTITIES ? 'Look up entity' : 'Look up inventory';
}

const _TOGGLE_BUTTON_OPTIONS = Object.values(LookupTableInsertType)
  .filter((k) => typeof k === 'string')
  .map((key: keyof typeof LookupTableInsertType) => ({
    buttonProps: {
      text: getUserTextForLookupTableType(LookupTableInsertType[key]),
    },
    dataTestAttrs: {
      component: 'LookupTableInsertModal',
      element: 'ToggleGroup',
      key,
    },
    id: LookupTableInsertType[key],
  }));

interface LookupTableInsertModalProps {
  // The callback for when the Modal is closed without submitting
  onRequestClose: () => void;

  // The callback for inserting the lookup table
  onRequestSubmit: (args: {
    schema: TagSchema | SchemaInterfaceExtendingSchema | StorableSchema;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
  }) => void;
}

function LookupTableInsertModal(props: LookupTableInsertModalProps): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const [selectedInsertType, setSelectedInsertType] = useState<LookupTableInsertType>(
    LookupTableInsertType.EXISTING_BIOENTITIES
  );
  const [selectedStorableSchema, setSelectedStorableSchema] = useState<StorableSchema | null>(null);
  const [selectedBioentitySchema, setSelectedBioentitySchema] = useState<
    TagSchema | SchemaInterfaceExtendingSchema | null
  >(null);
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});

  let insertModalPanel;
  let onRequestSubmit;
  switch (selectedInsertType) {
    case LookupTableInsertType.EXISTING_BIOENTITIES:
      insertModalPanel = (
        <>
          <FieldLabel label="Entity schema" />
          <TagSchemaOrInterfaceSelector
            onChange={setSelectedBioentitySchema}
            selectedTagSchemaOrInterfaceId={
              selectedBioentitySchema != null ? selectedBioentitySchema.id : null
            }
            tagSchemaFilterFn={(schema) =>
              isSchemaInterface(schema) || schema.folderItemType !== ModelTypeConstants.ENTRY
            }
            allowInterfaces={BenchlingConfig.SCHEMAS_ENABLE_INTERFACES}
          />
        </>
      );
      onRequestSubmit = () => {
        if (submitted) {
          // prevent double clicks
          return;
        }
        setSubmitted(true);
        assert(
          selectorsAreLoaded(selectorsById),
          'The submit button should be disabled until selectorsById is loaded'
        );
        assert(
          selectedBioentitySchema != null,
          'The submit button should be disabled until a schema is selected'
        );
        props.onRequestSubmit({
          schema: selectedBioentitySchema,
          selectorsById,
        });
      };

      break;
    case LookupTableInsertType.INVENTORY:
      insertModalPanel = (
        <>
          <FieldLabel label="Inventory schema" />
          <AllStorablesSchemaSelector
            onChange={(schemaOrFilter) => {
              if (!isSchemaTypeFilterOption(schemaOrFilter)) {
                setSelectedStorableSchema(schemaOrFilter);
              }
            }}
            selectedAllStorablesSchemaId={selectedStorableSchema != null ? selectedStorableSchema.id : null}
            shouldShowNoSchemaOption={false}
            includeStorableTypes={[
              ModelTypeConstants.BOX,
              ModelTypeConstants.CONTAINER,
              ModelTypeConstants.PLATE,
            ]}
          />
        </>
      );
      onRequestSubmit = () => {
        if (submitted) {
          // prevent double clicks
          return;
        }
        setSubmitted(true);
        assert(
          selectorsAreLoaded(selectorsById),
          'The submit button should be disabled until selectorsById is loaded'
        );
        props.onRequestSubmit({
          schema: selectedStorableSchema as StorableSchema,
          selectorsById,
        });
      };
      break;
    default:
      assert(false, 'Insert Type must be Bioentity or Storable');
  }
  return (
    <Modal
      title="Insert lookup table"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled:
            submitted ||
            !selectorsAreLoaded(selectorsById) ||
            (selectedInsertType === LookupTableInsertType.EXISTING_BIOENTITIES
              ? !selectedBioentitySchema
              : !selectedStorableSchema),
        },
      ]}
      className="mediocre" // TableEditable's CSS is namespaced under mediocre
      dataTestAttrs={{component: 'LookupTableInsertModal', element: 'root'}}
    >
      <FormGroup>
        <ToggleGroup<LookupTableInsertType>
          options={_TOGGLE_BUTTON_OPTIONS}
          selection={selectedInsertType}
          onChangeSelection={setSelectedInsertType}
          size={ButtonSize.MEDIUM}
        />
      </FormGroup>
      {insertModalPanel}
    </Modal>
  );
}

export default LookupTableInsertModal;
